import { forwardRef } from 'react';

import { useRender } from '@base-ui/react/use-render';
import { cn } from 'components/utils';

import {
  cardActionRecipe,
  cardDescriptionRecipe,
  cardFooterRecipe,
  cardLinkRecipe,
  cardMediaRecipe,
  cardMetaRecipe,
  cardRecipe,
  cardStatusRecipe,
  cardTextRecipe,
  cardTitleRecipe,
} from './Card.recipe';

import type { CardProps } from './types';

/**
 * A tile that opens one thing, from Figma set 2034:133442. SelectableCard's sibling: same surface,
 * corners, padding and layouts, but it goes somewhere rather than being chosen.
 *
 * Stretched link: the title renders the anchor, and its `::after` covers the card, so a click
 * anywhere opens it while cmd-click, middle-click and "copy link" still come from a real `<a>`.
 * `action` and `footer` sit above that overlay, never inside the anchor, so they stay valid HTML
 * and never navigate. The action is pinned absolutely, so it can follow the link in the DOM in
 * both layouts: tab order is link → action → footer.
 */
export const Card = forwardRef<HTMLAnchorElement, CardProps>(
  (
    {
      title,
      layout = 'tile',
      media,
      status,
      meta,
      description,
      footer,
      action,
      headingLevel = 3,
      className,
      render,
      ...linkProps
    },
    ref
  ) => {
    if (title.trim() === '') {
      throw new Error('Card requires a non-empty title.');
    }

    const Heading = `h${headingLevel}` as const;
    const link = useRender({
      ref,
      render: render ?? <a />,
      props: { ...linkProps, className: cardLinkRecipe(), children: title, 'data-part': 'link' },
    });
    const metaLines = Array.isArray(meta) ? meta : meta === undefined || meta === null ? [] : [meta];
    const hasAction = action !== undefined && action !== null;
    const hasMedia = media !== undefined && media !== null;
    const hasStatus = status !== undefined && status !== null;
    const hasFooter = footer !== undefined && footer !== null;
    const titleOnMedia =
      layout === 'row' && hasMedia && !hasStatus && metaLines.length === 0 && !description && !hasFooter;
    const anchor = (layout === 'tile' && hasMedia) || titleOnMedia ? 'media' : hasStatus ? 'status' : 'title';

    const actionNode = hasAction && (
      <div className={cardActionRecipe({ anchor })} data-anchor={anchor} data-card-actions="" data-part="action">
        {action}
      </div>
    );
    const footerNode = hasFooter && (
      <div className={cardFooterRecipe({ layout })} data-card-actions="" data-part="footer">
        {footer}
      </div>
    );

    return (
      <div className={cn(cardRecipe({ layout }), className)} data-layout={layout} data-part="root">
        {hasMedia && (
          <span aria-hidden="true" className={cardMediaRecipe()} data-part="media">
            {media}
          </span>
        )}
        <div
          className={cardTextRecipe({
            layout,
            clearAction: hasAction && (layout === 'row' || !hasMedia),
            titleOnMedia,
          })}
          data-part="text"
        >
          {hasStatus && (
            <div className={cardStatusRecipe()} data-part="status">
              {status}
            </div>
          )}
          <Heading className={cardTitleRecipe()} data-part="title">
            {link}
          </Heading>
          {metaLines.map((line, index) => (
            <p key={index} className={cardMetaRecipe()} data-part="meta">
              {line}
            </p>
          ))}
          {description && (
            <p className={cardDescriptionRecipe()} data-part="description">
              {description}
            </p>
          )}
          {actionNode}
          {layout === 'row' && footerNode}
        </div>
        {layout === 'tile' && footerNode}
      </div>
    );
  }
);
